/**
 * @module browser-storage-adapter
 * @description Implements the local-first file storage adapter used for demos,
 * prototyping, and Storybook scenarios. The adapter persists file metadata and
 * base64 payloads to guarded browser storage while following the shared contract.
 */

import {
  type BrowserStorage,
  getBrowserLocalStorage,
  isBrowserStorageQuotaError,
} from "../browser-storage";
import { StorageError } from "../errors";
import { warnRuntimeAdapterMock } from "../runtime-warning";
import type { FileStorageMetadata, StoredFile } from "../types";
import type {
  FileStorageAdapter,
  FileUploadOptions,
  LocalStorageFileAdapterOptions,
} from "./interface";

const FILES_KEY = "geenius_files";
const BLOBS_PREFIX = "geenius_blob_";
const DEFAULT_MAX_PAYLOAD_BYTES = 2 * 1024 * 1024;

interface StoredFileEntry extends StoredFile {
  blobKey: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isMetadataValue(
  value: unknown,
): value is FileStorageMetadata[keyof FileStorageMetadata] {
  return (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

function isFileStorageMetadata(value: unknown): value is FileStorageMetadata {
  return isRecord(value) && Object.values(value).every(isMetadataValue);
}

function isStoredFileEntry(value: unknown): value is StoredFileEntry {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "string" &&
    typeof value.name === "string" &&
    typeof value.path === "string" &&
    typeof value.size === "number" &&
    typeof value.mimeType === "string" &&
    typeof value.url === "string" &&
    (value.metadata === undefined || isFileStorageMetadata(value.metadata)) &&
    typeof value.createdAt === "string" &&
    typeof value.blobKey === "string"
  );
}

function parseStoredFiles(raw: string | null): StoredFileEntry[] {
  const parsed: unknown = JSON.parse(raw || "[]");
  if (!Array.isArray(parsed) || !parsed.every(isStoredFileEntry)) {
    throw new StorageError(
      "Invalid files payload in browser storage",
      "INVALID_KEY",
    );
  }
  return parsed;
}

function getFiles(): StoredFileEntry[] {
  try {
    const storage = getBrowserLocalStorage();
    return parseStoredFiles(storage.getItem(FILES_KEY));
  } catch (error) {
    if (error instanceof StorageError) {
      throw error;
    }
    if (error instanceof SyntaxError) {
      throw new StorageError(
        "Failed to parse files from browser storage",
        "INVALID_KEY",
        error,
      );
    }
    throw new StorageError(
      "Failed to read from browser storage",
      "SERVICE_ERROR",
      error,
    );
  }
}

function saveFiles(files: StoredFileEntry[]): void {
  try {
    const storage = getBrowserLocalStorage();
    storage.setItem(FILES_KEY, JSON.stringify(files));
  } catch (error) {
    if (isBrowserStorageQuotaError(error)) {
      throw new StorageError(
        "browser storage quota exceeded",
        "QUOTA_EXCEEDED",
        error,
      );
    }
    throw new StorageError(
      "Failed to write to browser storage",
      "INVALID_KEY",
      error,
    );
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    let reader: FileReader;
    try {
      reader = new FileReader();
    } catch (error) {
      reject(
        new StorageError(
          "Failed to initialize file reader",
          "UPLOAD_FAILED",
          error,
        ),
      );
      return;
    }

    reader.onload = () => {
      if (typeof reader.result !== "string") {
        reject(
          new StorageError(
            "Failed to convert file to data URL",
            "UPLOAD_FAILED",
          ),
        );
        return;
      }
      resolve(reader.result);
    };
    reader.onerror = () => {
      reject(
        new StorageError(
          "Failed to read file contents",
          "UPLOAD_FAILED",
          reader.error,
        ),
      );
    };
    try {
      reader.readAsDataURL(blob);
    } catch (error) {
      reject(
        new StorageError(
          "Failed to read file contents",
          "UPLOAD_FAILED",
          error,
        ),
      );
    }
  });
}

function base64ToBlob(dataUrl: string): Blob {
  try {
    const separatorIndex = dataUrl.indexOf(",");
    const meta = separatorIndex >= 0 ? dataUrl.slice(0, separatorIndex) : "";
    const data = separatorIndex >= 0 ? dataUrl.slice(separatorIndex + 1) : "";
    if (!meta.startsWith("data:") || separatorIndex < 0) {
      throw new Error("Invalid data URL payload");
    }
    const mime = meta.match(/:(.*?);/)?.[1] || "application/octet-stream";
    const bytes = atob(data);
    const buffer = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) buffer[i] = bytes.charCodeAt(i);
    return new Blob([buffer], { type: mime });
  } catch (error) {
    if (error instanceof StorageError) {
      throw error;
    }
    throw new StorageError(
      "Failed to decode file contents",
      "DOWNLOAD_FAILED",
      error,
    );
  }
}

function normalizeUploadOptions(
  nameOrOptions: string | FileUploadOptions | undefined,
): FileUploadOptions {
  if (typeof nameOrOptions === "string") {
    return { name: nameOrOptions };
  }
  const options = nameOrOptions ?? {};
  if (
    options.metadata !== undefined &&
    !isFileStorageMetadata(options.metadata)
  ) {
    throw new StorageError(
      "File metadata values must be string, number, boolean, or null",
      "INVALID_KEY",
    );
  }
  return options;
}

function resolveMaxPayloadBytes(
  options: LocalStorageFileAdapterOptions,
): number {
  const maxPayloadBytes = options.maxPayloadBytes ?? DEFAULT_MAX_PAYLOAD_BYTES;

  if (!Number.isInteger(maxPayloadBytes) || maxPayloadBytes <= 0) {
    throw new StorageError(
      "File payload limit must be a positive integer",
      "INVALID_KEY",
    );
  }

  return maxPayloadBytes;
}

function assertPayloadWithinLimit(file: Blob, maxPayloadBytes: number): void {
  if (file.size <= maxPayloadBytes) {
    return;
  }

  throw new StorageError(
    `File payload exceeds localStorage adapter limit of ${maxPayloadBytes} bytes`,
    "PAYLOAD_TOO_LARGE",
  );
}

function getStorageForOperation(
  operation: string,
  code: "DOWNLOAD_FAILED" | "SERVICE_ERROR" | "UPLOAD_FAILED",
): BrowserStorage {
  try {
    return getBrowserLocalStorage();
  } catch (error) {
    throw new StorageError(
      `Failed to access browser storage while ${operation}`,
      code,
      error,
    );
  }
}

function removeBlobPayload(
  storage: BrowserStorage,
  blobKey: string,
  code: "SERVICE_ERROR" | "UPLOAD_FAILED",
): void {
  try {
    storage.removeItem(blobKey);
  } catch (error) {
    throw new StorageError("Failed to remove stored file payload", code, error);
  }
}

function cleanupBlobPayload(storage: BrowserStorage, blobKey: string): void {
  try {
    storage.removeItem(blobKey);
  } catch {
    // Preserve the original persistence failure; cleanup is best-effort.
  }
}

/**
 * Creates the mock browser-storage-backed file adapter used in demos and Storybook.
 *
 * @param options - Optional local adapter limits for test and prototype payloads.
 * @returns A file-storage adapter implementation that persists metadata and payloads to browser storage.
 * @throws {StorageError} When file metadata or blob payloads cannot be persisted or resolved.
 */
export function createLocalStorageFileAdapter(
  options: LocalStorageFileAdapterOptions = {},
): FileStorageAdapter {
  const maxPayloadBytes = resolveMaxPayloadBytes(options);

  if (
    typeof process !== "undefined" &&
    process.env?.NODE_ENV === "production"
  ) {
    warnRuntimeAdapterMock(
      "[@geenius/adapters] browser storage file storage adapter is a MOCK for prototyping only. " +
        "Do NOT use in production. Use a real storage provider from @geenius/file-storage instead.",
    );
  }

  return {
    async upload(file, path, nameOrOptions?) {
      const options = normalizeUploadOptions(nameOrOptions);
      assertPayloadWithinLimit(file, maxPayloadBytes);
      const base64 = await blobToBase64(file);
      const id = crypto.randomUUID();
      const blobKey = BLOBS_PREFIX + id;

      const storage = getStorageForOperation("uploading file", "UPLOAD_FAILED");
      try {
        storage.setItem(blobKey, base64);
      } catch (error) {
        if (error instanceof StorageError) {
          throw error;
        }
        if (isBrowserStorageQuotaError(error)) {
          throw new StorageError(
            "browser storage quota exceeded",
            "QUOTA_EXCEEDED",
            error,
          );
        }
        throw new StorageError("Failed to store file", "INVALID_KEY", error);
      }

      const entry: StoredFileEntry = {
        id,
        name:
          options.name ||
          (file instanceof File ? file.name : `file-${id.slice(0, 8)}`),
        path,
        size: file.size,
        mimeType: file.type || "application/octet-stream",
        url: `local://${id}`,
        metadata: options.metadata,
        createdAt: new Date().toISOString(),
        blobKey,
      };
      try {
        const files = getFiles();
        files.push(entry);
        saveFiles(files);
      } catch (error) {
        cleanupBlobPayload(storage, blobKey);
        throw error;
      }

      const { blobKey: _, ...storedFile } = entry;
      return storedFile;
    },

    async download(fileId) {
      const entry = getFiles().find((f) => f.id === fileId);
      if (!entry)
        throw new StorageError(`File not found: ${fileId}`, "NOT_FOUND");
      const storage = getStorageForOperation(
        "downloading file",
        "DOWNLOAD_FAILED",
      );
      let base64: string | null;
      try {
        base64 = storage.getItem(entry.blobKey);
      } catch (error) {
        throw new StorageError(
          `Failed to read blob data for file: ${fileId}`,
          "DOWNLOAD_FAILED",
          error,
        );
      }
      if (!base64)
        throw new StorageError(
          `Blob data missing for file: ${fileId}`,
          "NOT_FOUND",
        );
      return base64ToBlob(base64);
    },

    async delete(fileId) {
      const files = getFiles();
      const idx = files.findIndex((f) => f.id === fileId);
      if (idx === -1) return false;
      const file = files[idx];
      if (!file) return false;
      const storage = getStorageForOperation("deleting file", "SERVICE_ERROR");
      const nextFiles = [...files.slice(0, idx), ...files.slice(idx + 1)];
      saveFiles(nextFiles);
      try {
        removeBlobPayload(storage, file.blobKey, "SERVICE_ERROR");
      } catch (error) {
        try {
          saveFiles(files);
        } catch {
          // The blob removal failure is the actionable delete error.
        }
        throw new StorageError(
          `Failed to delete blob data for file: ${fileId}`,
          "SERVICE_ERROR",
          error,
        );
      }
      return true;
    },

    async list(prefix?) {
      let files = getFiles();
      if (prefix) files = files.filter((f) => f.path.startsWith(prefix));
      return files.map(({ blobKey: _, ...f }) => f);
    },

    async getUrl(fileId) {
      const entry = getFiles().find((f) => f.id === fileId);
      if (!entry)
        throw new StorageError(`File not found: ${fileId}`, "NOT_FOUND");
      const storage = getStorageForOperation(
        "resolving file URL",
        "SERVICE_ERROR",
      );
      let base64: string | null;
      try {
        base64 = storage.getItem(entry.blobKey);
      } catch (error) {
        throw new StorageError(
          `Failed to resolve blob data for file: ${fileId}`,
          "SERVICE_ERROR",
          error,
        );
      }
      return base64 || entry.url;
    },
  };
}
